import api from '../../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FaFilter, FaTh, FaTable, FaPalette } from 'react-icons/fa'
import { FiInbox } from 'react-icons/fi'
import { HiDocumentChartBar } from 'react-icons/hi2'
import { VscClearAll } from 'react-icons/vsc'

/* Hooks */
import useMyPermission from '../../../hooks/useMyPermission'

/* form */
import Input from "../../../shared/components/form/input"
import Button from "../../../shared/components/Button/Button"
import ColorFilterModal from "../ColorFilterModal"
import SelectProviderModal from "../providerCatalog/SelectProviderModal"
import ProviderColorCatalog from "../providerCatalog/ProviderColorCatalog"
import ColorReportPicker from "../report/ColorReportPicker"
import getCostForCloth, { colorHasAnyCost } from "../../../shared/functions/getCostForCloth"
import getRefForCloth from "../../../shared/functions/getRefForCloth"

//list
import ListColorRow from '../listRow/ListColorImgRow'
import ListColorTableRow from '../listRow/ListColorTableRow'
//Modal
import Modal from '../../../shared/modal/Modal'

const EMPTY_FILTERS = { provider: '', cloth: '', colorType: '' }

function ListColor({ handleSelect, enableSelect, filterProvider, filterCloth }) {
    const [color, setColor] = useState([])
    const [providers, setProviders] = useState([])
    const [cloths, setCloths] = useState([])
    const [colorTypes, setColorTypes] = useState([])
    const [token] = useState(localStorage.getItem('token') || '')
    const [load, setLoad] = useState(false)

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: "", })
    const { checkPermission } = useMyPermission()
    const myPermissionNew = checkPermission(myPermission, "color_create")
    const myPermissionView = checkPermission(myPermission, "color_view")
    const myPermissionEdit = checkPermission(myPermission, "color_edit")
    const myPermissionDelete = checkPermission(myPermission, "color_delete")

    /* --------------------- search / filter --------------------- */
    const [search, setSearch] = useState('')
    const [modalFilter, setModalFilter] = useState(false)
    const [filters, setFilters] = useState(EMPTY_FILTERS)

    const hasLinkedFilter = !!(filterProvider && filterProvider !== 'none') || !!(filterCloth && filterCloth !== 'none')

    // Uma cor pode existir em vários tecidos, cada um com sua própria ref (se houver
    // exceção) e custo — então cada card da lista é um par cor×tecido, não a cor
    // inteira. Cor ainda sem tecido associado (`cloths: []`) entra como 1 card
    // "Sem tecido" (cloth: null), mesmo balde já usado pro custo/ref sem exceção.
    const dataFilter = useMemo(() => {
        const searchLowerCase = search.toLowerCase()

        const baseMatches = color.filter((item) => {
            const matchProvider = !filters.provider || item.provider?._id === filters.provider
            const matchColorType = !filters.colorType || item.colorType?._id === filters.colorType
            const matchLinkedProvider = !filterProvider || filterProvider === 'none' || item.provider?._id === filterProvider
            return matchProvider && matchColorType && matchLinkedProvider
        })

        const exploded = []
        baseMatches.forEach((item) => {
            const itemCloths = item.cloths && item.cloths.length > 0 ? item.cloths : [null]
            itemCloths.forEach((cloth) => {
                const clothId = cloth ? cloth._id : null
                exploded.push({
                    ...item,
                    _cardKey: `${item._id}-${clothId || 'sem-tecido'}`,
                    cloth: cloth || null,
                    ref: getRefForCloth(item, clothId),
                    costs: getCostForCloth(item, clothId),
                })
            })
        })

        return exploded.filter((row) => {
            const matchSearch = !search ||
                (row.name && row.name.toLowerCase().includes(searchLowerCase)) ||
                (row.ref && row.ref.toLowerCase().includes(searchLowerCase))
            const matchCloth = !filters.cloth || row.cloth?._id === filters.cloth
            const matchLinkedCloth = !filterCloth || filterCloth === 'none' || row.cloth?._id === filterCloth
            return matchSearch && matchCloth && matchLinkedCloth
            // Ordenado por tecido (cards "Sem tecido" vão pro final), depois por nome
            // da cor dentro do mesmo tecido — pedido explícito do usuário, no lugar
            // da ordem alfabética simples que vinha direto do backend.
        }).sort((a, b) => {
            const clothA = a.cloth?.name || '￿'
            const clothB = b.cloth?.name || '￿'
            const clothCompare = clothA.localeCompare(clothB)
            return clothCompare !== 0 ? clothCompare : a.name.localeCompare(b.name)
        })
    }, [color, search, filters, filterProvider, filterCloth])

    function handleSearch(e) {
        setSearch(e.target.value)
    }

    const providerOptions = useMemo(() => providers.map((p) => ({ value: p._id, label: p.name })), [providers])
    const clothOptions = useMemo(() => cloths.map((c) => ({ value: c._id, label: c.name })), [cloths])
    const colorTypeOptions = useMemo(() => colorTypes.map((c) => ({ value: c._id, label: c.name })), [colorTypes])

    const hasActiveFilter = !!search || !!filters.provider || !!filters.cloth || !!filters.colorType

    const colorsWithoutCostCount = useMemo(() => color.filter((item) => !colorHasAnyCost(item)).length, [color])

    const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }))
    const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch('') }

    //------------------ MODAL NEW ------------------------
    const [modalSelectProvider, setModalSelectProvider] = useState(false)
    const [modalProviderCatalog, setModalProviderCatalog] = useState(false)
    const [catalogProvider, setCatalogProvider] = useState(null)
    const [modalReport, setModalReport] = useState(false)
    const [viewMode, setViewMode] = useState('cards')

    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    const getAll = () => {
        api.get(`color/getall`, {
            headers: {
                Authorization: `Bearer ${JSON.parse(token)}`
            }
        }).then((response) => {
            if (response.data) {
                setColor(response.data.colors)
                setProviders(response.data.providers)
                setCloths(response.data.cloths)
                setColorTypes(response.data.colorTypes)
                setMyPermission(response.data.permission)
                setLoad(true)
            }
        })
    }

    const listUpdate = () => {
        getAll()
    }

    function openCatalogFlow() {
        setModalSelectProvider(true)
    }

    function handleSelectProviderContinue(provider) {
        setCatalogProvider(provider)
        setModalSelectProvider(false)
        setModalProviderCatalog(true)
    }

    function closeCatalogFlow() {
        setModalProviderCatalog(false)
        getAll()
    }

    if (!load) {
        return <div className="pg-loading">Carregando cores...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Catálogo de Cores</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionNew && !enableSelect && (
                                <Button variant="new" onClick={openCatalogFlow}>Cadastrar no Catálogo</Button>
                            )}
                            {!enableSelect && (
                                <Button variant="muted" icon={HiDocumentChartBar} onClick={() => setModalReport(true)}>Relatório</Button>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <div className="pg-view-toggle">
                                <button
                                    type="button"
                                    className={`pg-view-toggle-btn ${viewMode === 'cards' ? 'pg-view-toggle-btn-active' : ''}`}
                                    onClick={() => setViewMode('cards')}
                                    title="Ver como cards"
                                >
                                    <FaTh size={12} />
                                    <span>Cards</span>
                                </button>
                                <button
                                    type="button"
                                    className={`pg-view-toggle-btn ${viewMode === 'table' ? 'pg-view-toggle-btn-active' : ''}`}
                                    onClick={() => setViewMode('table')}
                                    title="Ver como tabela"
                                >
                                    <FaTable size={12} />
                                    <span>Tabela</span>
                                </button>
                            </div>
                            <button
                                className={`pg-filter-btn ${hasActiveFilter ? 'pg-filter-btn-active' : ''}`}
                                onClick={() => setModalFilter(true)}
                            >
                                <FaFilter size={13} />
                                <span>Filtrar</span>
                                {hasActiveFilter && <span className="pg-filter-dot" />}
                            </button>
                            <div className="pg-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar por nome ou ref..."
                                    value={search}
                                    handleOnChange={handleSearch}
                                />
                            </div>
                            <span className="pg-count">
                                {dataFilter.length} {dataFilter.length === 1 ? 'cor' : 'cores'}
                            </span>
                        </div>
                    </div>
                </div>

                {hasLinkedFilter && (
                    <div className="pg-filter-active-bar">
                        <span className="pg-filter-active-dot" />
                        <span className="pg-filter-active-label">Mostrando apenas cores do fornecedor/tecido do produto selecionado</span>
                    </div>
                )}

                {hasActiveFilter && (
                    <div className="pg-filter-active-bar">
                        <span className="pg-filter-active-dot" />
                        <span className="pg-filter-active-label">Filtro ativo</span>
                        <span className="pg-filter-active-count">
                            {dataFilter.length} {dataFilter.length === 1 ? 'cor encontrada' : 'cores encontradas'}
                        </span>
                        <button className="pg-filter-active-clear" onClick={clearFilters}>
                            <VscClearAll size={14} />
                            Limpar
                        </button>
                    </div>
                )}

                {colorsWithoutCostCount > 0 && (
                    <div className="pg-alert-bar">
                        <FaPalette size={15} className="pg-alert-bar-icon" />
                        <span className="pg-alert-bar-label">
                            {colorsWithoutCostCount} {colorsWithoutCostCount === 1 ? 'cor sem preço definido' : 'cores sem preço definido'}
                        </span>
                    </div>
                )}
            </div>

            <div className="pg-list">
                {dataFilter.length > 0 ? (
                    viewMode === 'table' ? (
                        <ListColorTableRow dataArray={dataFilter} handleOnSuccess={listUpdate} enableSelect={enableSelect} handleSelect={handleSelect} myPermissionEdit={myPermissionEdit} myPermissionDelete={myPermissionDelete} />
                    ) : (
                        <ListColorRow dataArray={dataFilter} handleOnSuccess={listUpdate} enableSelect={enableSelect} handleSelect={handleSelect} myPermissionEdit={myPermissionEdit} myPermissionDelete={myPermissionDelete} />
                    )
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhuma cor encontrada</p>
                        <p className="pg-empty-subtitle">
                            {color.length === 0
                                ? 'Cadastre a primeira cor para começar.'
                                : 'Tente ajustar a busca ou os filtros.'}
                        </p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalSelectProvider}
                changeStatus={setModalSelectProvider}
                w={45}
                content={
                    <SelectProviderModal providers={providers} handleOnContinue={handleSelectProviderContinue} />
                }
            />

            <Modal
                modalStatus={modalProviderCatalog}
                changeStatus={closeCatalogFlow}
                w={92}
                t={3}
                h={90}
                content={
                    catalogProvider && <ProviderColorCatalog provider={catalogProvider} colorTypes={colorTypes} />
                }
            />

            <Modal
                modalStatus={modalReport}
                changeStatus={setModalReport}
                w={45}
                content={
                    <ColorReportPicker providers={providers} btCancelAction={() => setModalReport(false)} />
                }
            />

            {modalFilter && (
                <ColorFilterModal
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={clearFilters}
                    onClose={() => setModalFilter(false)}
                    providerOptions={providerOptions}
                    clothOptions={clothOptions}
                    colorTypeOptions={colorTypeOptions}
                />
            )}

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar cores</p>
        </div>
    )
}

export default ListColor
